import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import carouselItem from "../assets/images/trading/buy_sell_carousel_item.svg";
import { PlusIcon } from "lucide-react";
import { useFeatureFlagsByCategory } from "../hooks/query/queries";

const DashboardCarousel = () => {
  const navigate = useNavigate();
  const [activeSlide, setActiveSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const { data: featureFlags } = useFeatureFlagsByCategory("mainFeatures");

  const slides = useMemo(() => {
    return [
      featureFlags?.deposit && {
        id: 0,
        title: "Welcome to 80eight!",
        description:
          "To get started, easily deposit money from your bank account or receive crypto using your wallet address.\n\n Once your balance is topped up, you're ready to start trading or investing.",
        image: carouselItem,
        buttonText: "Deposit Cash",
        buttonText2: "Deposit Crypto",
      },
      {
        id: 1,
        title: "Digital Wallet",
        description:
          "A calmer way to manage your money.\n Crypto, cash, and payments all in one place.",
        image: carouselItem,
        buttonText2: "Explore Digital Wallet",
      },
      featureFlags?.eightPay && {
        id: 2,
        title: "88Pay",
        description:
          "Send cash or crypto to anyone on 80eight.\n Instant and free.",
        image: carouselItem,
        buttonText: "Deposit Cash",
        buttonText2: "Explore Digital Wallet",
      },
      featureFlags?.autostack && {
        id: 3,
        title: "AutoStack",
        description:
          "Automate Your Crypto Buys. Set a schedule to buy crypto automatically, so you build over time without lifting a finger.",
        image: carouselItem,
        buttonText: "Deposit Cash",
        buttonText2: "Explore AutoStack",
      },
    ].filter(Boolean);
  }, [featureFlags]);

  useEffect(() => {
    if (!isPaused) {
      intervalRef.current = setInterval(() => {
        setActiveSlide((prev) => (prev + 1) % slides.length);
      }, 8000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPaused, slides.length]);

  const handleDotClick = (index: number) => {
    setActiveSlide(index);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const handleButtonClick = (buttonText: string) => {
    switch (buttonText) {
      case "Deposit Cash":
        navigate("/trading/digitalWallet", {
          state: { parentTab: "withdraw-deposit", selectedTab: "deposit" },
        });
        break;
      case "Deposit Crypto":
        navigate("/trading/digitalWallet", {
          state: { parentTab: "withdraw-deposit", selectedTab: "deposit" },
        });
        break;
      case "Explore Digital Wallet":
        navigate("/trading/digitalWallet");
        break;
      case "Explore AutoStack":
        navigate("/autostack");
        break;
      default:
        console.warn(`Unknown button text: ${buttonText}`);
    }
  };

  const currentSlide = slides.at(activeSlide) ?? slides[0];

  return (
    <div className="md:min-h-[635px] bg-white rounded-lg p-6 flex justify-center items-center">
      <div
        className="flex flex-col justify-between"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="flex-1 flex flex-col items-center justify-center text-center space-y-4 max-w-[556px]">
          <div className="flex items-center justify-center mb-2">
            <img
              src={currentSlide.image}
              alt={currentSlide.title}
              className="w-28 lg:w-[160px]"
            />
          </div>

          <h3 className="text-lg lg:text-[24px] font-semibold text-[#33125F]">
            {currentSlide.title}
          </h3>

          <p className="text-sm text-gray-600 leading-relaxed max-w-lg whitespace-pre-line">
            {currentSlide.description}
          </p>
          <div className="flex flex-col justify-center items-center sm:flex-row gap-4 mt-4 w-full">
            {currentSlide.buttonText && (
              <button
                onClick={() => handleButtonClick(currentSlide.buttonText)}
                className="h-[48px] flex items-center justify-center w-full lg:!w-[149px] bg-white border border-[#EBE9EF] text-[#6D6584] rounded-full text-[14px] font-semibold hover:bg-gray-50 transition-colors"
              >
                {currentSlide.buttonText}
              </button>
            )}
            {currentSlide.buttonText2 && (
              <button
                onClick={() => handleButtonClick(currentSlide.buttonText2)}
                className="h-[48px] flex items-center justify-center w-full lg:!w-[220px] bg-white text-[white] rounded-full !text-[14px] font-semibold btn-primary gap-2 hover:opacity-90 transition-opacity"
              >
                <PlusIcon className="w-3 h-3 md:w-4 md:h-4" />
                {currentSlide.buttonText2}
              </button>
            )}
          </div>
        </div>

        <div className="flex justify-center space-x-2 mt-20">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => handleDotClick(index)}
              className={`w-2 h-2 rounded-full transition-colors duration-800 ${
                index === activeSlide ? "bg-brand-light w-6" : "bg-gray-300"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default DashboardCarousel;
